import {isFeatureEnabled} from '@github-ui/feature-flags'
import {updateCurrentState} from '@github-ui/history'
import {matchRoutes} from '@github-ui/react-router'
import {SOFT_NAV_STATE} from '@github-ui/soft-nav/states'

interface ReactApp extends HTMLElement {
  navigate?: (pathname: string) => Promise<void>
  routes?: never[]
  uuid: string
}

declare global {
  interface Window {
    Turbo: {
      PageSnapshot: {
        fromElement: (element: Element | null) => {clone: () => unknown}
      }
      navigator: {
        view: {
          snapshotCache: {
            put: (url: Location, snapshot: unknown) => void
            keys: Location[]
          }
        }
      }
      session: {
        view: {
          lastRenderedLocation: URL
        }
      }
    }
  }
}

export function reactNavigateIfPossible(event: TurboClickEvent | TurboFrameClickEvent) {
  if (!(event.target instanceof HTMLElement)) return

  const target = event.target

  const reactAppName = target.getAttribute('data-react-nav')

  if (!reactAppName || !isFeatureEnabled('allow_react_navs_in_turbo')) return false

  // Try to use React navigation if the react app is loaded
  const reactApp = Array.from(document.querySelectorAll<ReactApp>('react-app')).find(
    app => app.getAttribute('app-name') === reactAppName,
  )

  if (!reactApp) return false

  const anchorId = target.getAttribute('data-react-nav-anchor')

  const firedReactNav = anchorId
    ? clickOnReactAnchor({event, reactApp, anchorId})
    : imperativelyNavigateToReactRoute({event, reactApp})

  if (!firedReactNav) return false

  const handleSoftNavEnd = () => {
    reactApp.hidden = false

    const elementsToRemove = target.getAttribute('data-react-nav-remove')?.split(',') || []
    if (elementsToRemove.length === 0) return firedReactNav

    for (const elementId of elementsToRemove) {
      const element = document.querySelector(`#${elementId}`)
      if (element) element.remove()
    }

    document.removeEventListener(SOFT_NAV_STATE.ERROR, handleSoftNavError)
  }

  const handleSoftNavError = () => {
    document.removeEventListener(SOFT_NAV_STATE.END, handleSoftNavEnd)
  }

  document.addEventListener(SOFT_NAV_STATE.END, handleSoftNavEnd, {once: true})
  document.addEventListener(SOFT_NAV_STATE.ERROR, handleSoftNavError, {once: true})

  return firedReactNav
}

function clickOnReactAnchor({
  event,
  reactApp,
  anchorId,
}: {
  event: TurboClickEvent | TurboFrameClickEvent
  reactApp: ReactApp
  anchorId: string
}) {
  const anchor = document.getElementById(anchorId) as HTMLAnchorElement | null

  if (!anchor) return false

  anchor.click()
  preventTurboNavigation({event, href: anchor.href, app: reactApp})
  return true
}

function imperativelyNavigateToReactRoute({
  event,
  reactApp,
}: {
  event: TurboClickEvent | TurboFrameClickEvent
  reactApp: ReactApp
}) {
  const url = new URL(event.detail.url, window.location.origin)
  const pathname = url.pathname + url.search + url.hash

  const routes = reactApp.routes
  if (!routes || !Array.isArray(routes) || routes.length === 0) return false

  try {
    // Check if the URL matches any routes in the react app
    const matchedRoutes = matchRoutes(routes, url.pathname)

    if (!matchedRoutes || matchedRoutes.length === 0) {
      // Route is not part of the react app, fall back to Turbo navigation
      return false
    }

    if (!reactApp.navigate) return false

    // NOTE: This code interacts with the router/history directly, which is not officially supported
    // by React Router and could break if the underlying libraries change. As we migrate more toward
    // full React pages, usage of this pattern should decrease. If it does break in the future, we’ll
    // fallback to a turbo navigation to avoid impacting user experience.
    reactApp.navigate(pathname)
    preventTurboNavigation({event, href: url.href, app: reactApp})
    return true
  } catch {
    return false
  }
}

function preventTurboNavigation({
  event,
  href,
  app,
}: {
  event: TurboClickEvent | TurboFrameClickEvent
  href: string
  app: ReactApp
}) {
  event.preventDefault() // prevent Turbo navigation
  event.detail.originalEvent?.preventDefault() // prevent the original link click

  if (event.target instanceof HTMLElement && event.target.getAttribute('data-react-nav-turbo-cache') === 'true') {
    prepareTurboCache(href)
    restoreAppId(app)
  }
}

function restoreAppId(app: ReactApp) {
  document.addEventListener(
    SOFT_NAV_STATE.END,
    () => {
      updateCurrentState({appId: app.uuid})
    },
    {once: true},
  )
}

function prepareTurboCache(href: string) {
  updateCurrentState({appId: 'rails'})
  const {PageSnapshot, navigator, session} = window.Turbo

  const snapshot = PageSnapshot.fromElement(document.querySelector('turbo-frame')).clone()
  navigator.view.snapshotCache.put(window.location, snapshot)
  session.view.lastRenderedLocation = new URL(href, window.location.origin)
}
